import React from 'react';
import PropTypes from 'prop-types';

import {Box, Pill, Stack, Typography} from 'views/components';
import {SimpleRefreshWidget, translate} from 'views/module';

import moment from 'moment';
import {simplifyQuestions} from 'utils/user_bio';

const NULL_CHAR = '-';

// The header of a Loom caregiver. It follows the caregiver view's header, but it is read-only:
// Loom has no CareOS route that changes a caregiver, so there is no edit, account or Dialpad action.
const LoomCaregiverHeader = (props) => {
  const {caregiver, refresh, setRefresh} = props;

  const [lastUpdatedAt, setLastUpdatedAt] = React.useState(new Date());

  const caregiverBio = React.useMemo(() => {
    return simplifyQuestions(caregiver.caregiverBio?.questions || {});
  }, [caregiver]);

  const caregiverDetail = caregiver.caregiverDetail;
  const location = [caregiverBio.city, caregiverBio.province].filter(Boolean).join(', ');
  const statusTitle = translate(`caregiver.LoomStatus.${caregiver.loomStatus}.Title`);

  const sections = [
    {
      key: 'phoneNumber',
      label: translate('caregiver.CaregiverDetail.Status.Metrics.PhoneNumber'),
      value: caregiverBio.phoneNumber || NULL_CHAR,
    },
    {
      key: 'email',
      label: translate('caregiver.CaregiverDetail.Status.Metrics.Email'),
      value: caregiverBio.email || NULL_CHAR,
    },
    {
      key: 'legacyRn',
      label: translate('caregiver.LoomCaregiverDetail.Header.LegacyRn'),
      value: caregiver.caregiverRn || translate('caregiver.LoomCaregiverDetail.Header.NoLegacyRn'),
    },
  ];

  return (
    <Stack gap={1}>
      <Typography sx={{fontSize: '28px', fontWeight: 400}}>
        {caregiverDetail.caregiverName || NULL_CHAR}
      </Typography>
      <Stack sx={{gap: '10px'}}>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems={{xs: 'flex-start', md: 'center'}}
          sx={{gap: '10px', flexWrap: 'wrap'}}
        >
          <Stack
            direction='row'
            sx={{
              gap: '7px',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Pill
              label={`● ${statusTitle}`}
              variant='warning'
            />
            {
              location &&
              <Pill
                label={location}
                variant='neutral'
              />
            }
            {
              caregiverDetail.createdAt &&
              <Pill
                label={translate('caregiver.CaregiverDetail.Status.Subtitles.Created', {
                  value: moment(caregiverDetail.createdAt).format(translate('timeFormat.ShortMonthDayYear')),
                })}
                variant='neutral'
              />
            }
            <Pill
              label={translate('caregiver.CaregiverDetail.Status.Subtitles.Updated', {
                value: moment(lastUpdatedAt).format(translate('timeFormat.ShortMonthDayTime')),
              })}
              variant='neutral'
            />
          </Stack>
          <Stack
            direction='row'
            alignItems='center'
            sx={{gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end', ml: 'auto'}}
          >
            <SimpleRefreshWidget
              numRefresh={refresh}
              setNumRefresh={setRefresh}
              setLastUpdatedAt={setLastUpdatedAt}
            />
          </Stack>
        </Stack>
        <Box
          sx={{
            boxSizing: 'border-box',
            width: '100%',
            backgroundColor: '#FFFFFF',
            border: '1px solid #E8E4DE',
            borderRadius: '12px',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gap: '1px',
              backgroundColor: '#E8E4DE',
              gridTemplateColumns: {
                xs: 'repeat(1, minmax(0, auto))',
                sm: 'repeat(3, minmax(0, auto))',
              },
            }}
          >
            {
              sections.map((section) => (
                <Box
                  key={section.key}
                  sx={{
                    minWidth: 0,
                    backgroundColor: '#FFFFFF',
                    px: 3,
                    py: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Stack
                    sx={{
                      gap: '6px',
                      minWidth: 0,
                      alignItems: 'flex-start',
                    }}
                  >
                    <Typography sx={{fontWeight: 500, fontSize: '11px', lineHeight: '14px', color: '#A8A49E'}}>
                      {section.label}
                    </Typography>
                    <Typography sx={{fontWeight: 700, fontSize: '12px', lineHeight: '16px', color: '#000000', wordBreak: 'break-word'}}>
                      {section.value}
                    </Typography>
                  </Stack>
                </Box>
              ))
            }
          </Box>
        </Box>
      </Stack>
    </Stack>
  );
};
LoomCaregiverHeader.propTypes = {
  caregiver: PropTypes.object.isRequired,
  refresh: PropTypes.number.isRequired,
  setRefresh: PropTypes.func.isRequired,
};

export default React.memo(LoomCaregiverHeader);
